import { useEffect, useRef, useState } from "react";
import type { ComponentPropsWithoutRef, PointerEvent as ReactPointerEvent, ReactNode } from "react";

export type SwipeDismissDialogProps = ComponentPropsWithoutRef<"section"> & {
  children: ReactNode;
  onDismiss: () => void;
};

type Gesture = { id: number; x: number; y: number; startedAt: number; vertical: boolean | null };

export function SwipeDismissDialog({ children, className = "", onDismiss, ...props }: SwipeDismissDialogProps) {
  const dialogRef = useRef<HTMLElement>(null);
  const gesture = useRef<Gesture | null>(null);
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);

  const start = (id: number, x: number, y: number, target: EventTarget | null) => {
    gesture.current = null;
    if (!(target instanceof Element) || target.closest("input,textarea,select")) return;
    // A gesture that begins inside a nested sheet belongs only to that sheet.
    // Without this guard the same bubbling touch can dismiss every parent layer.
    if (target.closest(".swipe-dismiss-dialog") !== dialogRef.current) return;
    const fromHandle = target.closest(".swipe-dismiss-handle");
    let element: Element | null = target;
    while (!fromHandle && element) {
      if (element.scrollHeight > element.clientHeight + 1 && element.scrollTop > 0) return;
      if (element === dialogRef.current) break;
      element = element.parentElement;
    }
    gesture.current = { id, x, y, startedAt: performance.now(), vertical: null };
  };
  const move = (id: number, x: number, y: number) => {
    const current = gesture.current;
    if (!current || current.id !== id) return false;
    const dy = y - current.y, dx = x - current.x;
    if (current.vertical === null && (Math.abs(dy) > 8 || Math.abs(dx) > 8)) {
      current.vertical = dy > 0 && Math.abs(dy) > Math.abs(dx);
    }
    if (!current.vertical) return false;
    setDragging(true);
    setOffset(Math.min(Math.max(0, dy), window.innerHeight * 0.7));
    return true;
  };
  const finish = (id: number, y: number, cancelled = false) => {
    const current = gesture.current;
    if (!current || current.id !== id) return;
    const dy = Math.max(0, y - current.y);
    const elapsed = Math.max(1, performance.now() - current.startedAt);
    gesture.current = null;
    setDragging(false); setOffset(0);
    if (!cancelled && current.vertical && (dy >= 90 || (dy >= 45 && dy / elapsed > 0.45))) dismissRef.current();
  };

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const touchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (event.touches.length !== 1 || !touch) { gesture.current = null; setDragging(false); setOffset(0); return; }
      start(touch.identifier, touch.clientX, touch.clientY, event.target);
    };
    const touchMove = (event: TouchEvent) => {
      const touch = Array.from(event.touches).find(item => item.identifier === gesture.current?.id);
      if (touch && move(touch.identifier, touch.clientX, touch.clientY) && event.cancelable) event.preventDefault();
    };
    const touchEnd = (event: TouchEvent) => {
      const touch = Array.from(event.changedTouches).find(item => item.identifier === gesture.current?.id);
      if (touch) finish(touch.identifier, touch.clientY, event.type === "touchcancel");
    };
    // Non-passive touchmove retains a downward dismiss gesture instead of letting
    // browser scrolling cancel pointer events. Upward gestures still scroll normally.
    dialog.addEventListener("touchstart", touchStart, { passive: true });
    dialog.addEventListener("touchmove", touchMove, { passive: false });
    dialog.addEventListener("touchend", touchEnd);
    dialog.addEventListener("touchcancel", touchEnd);
    return () => {
      dialog.removeEventListener("touchstart", touchStart);
      dialog.removeEventListener("touchmove", touchMove);
      dialog.removeEventListener("touchend", touchEnd);
      dialog.removeEventListener("touchcancel", touchEnd);
    };
  }, []);
  const pointerStart = (event: ReactPointerEvent<HTMLElement>) => {
    event.stopPropagation();
    if (event.pointerType === "touch" || event.button !== 0) return;
    start(event.pointerId, event.clientX, event.clientY, event.target);
  };
  const pointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    event.stopPropagation();
    if (event.pointerType === "touch") return;
    if (move(event.pointerId, event.clientX, event.clientY)) {
      event.preventDefault();
      if (!event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.setPointerCapture(event.pointerId);
    }
  };
  const pointerFinish = (event: ReactPointerEvent<HTMLElement>) => {
    event.stopPropagation();
    if (event.pointerType !== "touch") finish(event.pointerId, event.clientY, event.type === "pointercancel");
  };
  return <section {...props} ref={dialogRef} className={`${className} swipe-dismiss-dialog${dragging ? " is-dragging" : ""}`} style={{ ...props.style, transform: `translateY(${offset}px)` }} onPointerDown={pointerStart} onPointerMove={pointerMove} onPointerUp={pointerFinish} onPointerCancel={pointerFinish}>
    <div className="swipe-dismiss-handle" aria-hidden="true" />
    {children}
  </section>;
}
